import { formatNotificationDate, getNotificationAction } from "@/lib/utils";
import { Notification as NotificationType } from "@/redux/services/notification/notification.type";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import OrderDetailsDrawer from "./order/OrderDetailsDrawer";
import { useNotifications } from "@/context/NotificationsContext";
import {
  X,
  ShoppingCart,
  Bell,
  CheckCircle,
  Circle,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";

type NotificationPanelProps = {
  isOpen: boolean;
  onClose: () => void;
};

type NotificationProps = {
  notification: NotificationType;
  onMarkAsRead: (id: string, isRead: boolean) => void;
};

const getNotificationIcon = (action: string) => {
  if (action.startsWith("notifications.inventory.")) {
    return <AlertTriangle className="h-4 w-4" />;
  }
  if (action.includes("order")) {
    return <ShoppingCart className="h-4 w-4" />;
  }
  return <Bell className="h-4 w-4" />;
};

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const { unreadCount, notifications, changeReadStatus } = useNotifications();

  const handleMarkAllAsRead = () => {
    notifications
      .filter((n) => !n.isRead)
      .forEach((n) => changeReadStatus(n.id, true));
  };

  return (
    <div
      className={cn(
        "fixed inset-y-0 right-0 z-50 w-full md:w-[400px] bg-card shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}
    >
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-card-foreground">Notifications</h2>
          {unreadCount > 0 && (
            <span className="bg-primary text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs text-primary font-medium hover:underline"
            >
              Tout marquer lu
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
            aria-label="Fermer les notifications"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Notifications list */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Bell className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Aucune notification</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={changeReadStatus}
            />
          ))
        )}
      </div>
    </div>
  );
};

const NotificationItem: React.FC<NotificationProps> = ({
  notification,
  onMarkAsRead,
}) => {
  const { id, action, metadata, isRead, createdAt } = notification;
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const navigate = useNavigate();

  const onClickOrderDetails = () => {
    !isRead && onMarkAsRead(id, true);
    setShowOrderDetails(true);
  };

  const isInventoryNotification = action.startsWith("notifications.inventory.");
  const isInventoryOrderFailure = [
    "notifications.inventory.availability_shortfall",
    "notifications.inventory.reservation_failed",
    "notifications.inventory.commit_failed",
    "notifications.inventory.release_failed",
    "notifications.inventory.restore_failed",
  ].includes(action);

  const onClickInventory = () => {
    !isRead && onMarkAsRead(id, true);
    if (action === "notifications.inventory.low_stock") {
      navigate("/inventory/ingredients");
      return;
    }
    if (isInventoryOrderFailure && metadata.order_uuid) {
      setShowOrderDetails(true);
      return;
    }
    navigate("/inventory");
  };

  return (
    <div
      className={cn(
        "px-5 py-4 border-b border-border transition-colors hover:bg-muted/50",
        !isRead && "bg-primary-light/30"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          "p-2 rounded-lg flex-shrink-0 mt-0.5",
          !isRead ? "bg-primary-light text-primary" : "bg-muted text-muted-foreground"
        )}>
          {getNotificationIcon(action)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={cn(
            "text-sm",
            !isRead ? "text-card-foreground font-medium" : "text-muted-foreground"
          )}>
            {getNotificationAction(notification)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatNotificationDate(createdAt)}
          </p>
          {[
            "notifications.order.new_order",
            "notifications.order.customer_update",
          ].includes(action) && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={onClickOrderDetails}
            >
              Afficher
            </Button>
          )}
          {isInventoryNotification && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={onClickInventory}
            >
              Afficher
            </Button>
          )}
        </div>

        {/* Read/unread toggle */}
        <button
          className="p-1 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
          aria-label={isRead ? "Marquer comme non lu" : "Marquer comme lu"}
          onClick={() => onMarkAsRead(id, !isRead)}
        >
          {isRead ? (
            <CheckCircle className="h-4 w-4 text-success" />
          ) : (
            <Circle className="h-4 w-4" />
          )}
        </button>
      </div>

      {showOrderDetails && (
        <OrderDetailsDrawer
          orderId={metadata.id ?? metadata.order_uuid}
          orderNumber={metadata.order_number}
          isOpen={showOrderDetails}
          onClose={() => setShowOrderDetails(false)}
        />
      )}
    </div>
  );
};

export default NotificationPanel;
